import ContentManager from "./ContentManager";
import { useState } from "react";
import { Sparkles, Link2, ListTree, Loader2 } from "lucide-react";
import { adminAutoLink, adminGenerateSeoMeta, adminGenerateFaqs } from "../../lib/api";

function BlogEditToolbar({ post, refresh, setForm }) {
  const [busy, setBusy] = useState(null);
  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      const result = await fn(post.id);
      // Reflect server-side changes immediately in the open form so editor sees the result.
      if (key === "auto-link" && typeof result.links_added === "number") {
        // Refetch the full item by reloading the list (refresh closes form, so update form too).
        alert(`Added ${result.links_added} internal links: ${result.linked_terms.join(", ") || "(none matched)"}`);
      } else if (key === "seo") {
        setForm((f) => ({ ...f, seo_title: result.seo_title, seo_description: result.seo_description }));
      } else if (key === "faqs") {
        setForm((f) => ({ ...f, faq_items: result.faq_items }));
      }
      if (successMsg) console.log(successMsg);
    } catch (e) {
      alert(e?.response?.data?.detail || "Operation failed");
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => run("auto-link", adminAutoLink, "Auto-link complete")}
        disabled={!!busy}
        className="btn-ghost text-xs px-3 py-2"
        data-testid="blog-edit-autolink"
        title="Add markdown links to city & accident-type pages mentioned in your content (first mention only)."
      >
        {busy === "auto-link" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
        Auto-link
      </button>
      <button
        type="button"
        onClick={() => run("seo", adminGenerateSeoMeta)}
        disabled={!!busy}
        className="btn-ghost text-xs px-3 py-2"
        data-testid="blog-edit-generate-seo"
        title="Generate SEO title + meta description from your content (AI)."
      >
        {busy === "seo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        Generate SEO
      </button>
      <button
        type="button"
        onClick={() => run("faqs", adminGenerateFaqs)}
        disabled={!!busy}
        className="btn-ghost text-xs px-3 py-2"
        data-testid="blog-edit-generate-faqs"
        title="Generate 5-7 schema-ready FAQ items from your content (AI)."
      >
        {busy === "faqs" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListTree className="w-3.5 h-3.5" />}
        Generate FAQs
      </button>
    </>
  );
}

export function BlogManager() {
  return (
    <ContentManager
      resource="blog/posts"
      title="Blog Posts"
      description="Write manually or generate an AI draft. Markdown supported. Move posts through Draft → In Review → Scheduled → Published."
      enableAI
      enableDuplicate
      enableQuickPublish
      editToolbar={(post, refresh, setForm) =>
        <BlogEditToolbar post={post} refresh={refresh} setForm={setForm} />
      }
      duplicateFields={[
        { key: "title", label: "New title", placeholder: "Different title for the duplicate" },
        { key: "slug", label: "URL slug (optional)", placeholder: "auto-generated if blank" },
      ]}
      defaults={{
        status: "draft", published: false, auto_publish: false,
        read_time: 5, author: "Ohio Accident Aid Team", tags: [], faq_items: [],
      }}
      fields={[
        { key: "title", label: "Title", full: true },
        { key: "slug", label: "URL Slug", hint: "lowercase-with-dashes (auto-generated if blank)" },
        { key: "category", label: "Category", placeholder: "e.g., Car Accidents" },
        {
          key: "status", label: "Editorial Status", type: "select",
          options: [
            { value: "draft", label: "Draft (only visible in admin)" },
            { value: "in_review", label: "In Review" },
            { value: "scheduled", label: "Scheduled" },
            { value: "published", label: "Published (live on site)" },
          ],
          hint: "Only 'published' posts appear on the public site.",
        },
        { key: "scheduled_at", label: "Scheduled Publish Time", type: "datetime", hint: "Used when Status = Scheduled." },
        { key: "auto_publish", label: "Auto-publish at scheduled time (default OFF — requires approval)", type: "boolean" },
        { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2, full: true },
        { key: "content", label: "Content (Markdown supported — paste articles written elsewhere)", type: "textarea", rows: 22, full: true },
        { key: "tags", label: "Tags", type: "tags" },
        { key: "cover_image", label: "Featured / Cover Image", type: "image", full: true },
        { key: "author", label: "Author" },
        { key: "read_time", label: "Read Time (minutes)", type: "number" },
        { key: "seo_title", label: "SEO Title", hint: "Aim for under 60 chars. Use 'Generate SEO' above to auto-fill." },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2, full: true, hint: "Aim for under 160 chars." },
        { key: "canonical_url", label: "Canonical URL (optional)", hint: "Use only when this content lives elsewhere as the primary source." },
        { key: "featured_image_prompt", label: "Featured Image Prompt (for Midjourney / Nano Banana / DALL-E)", type: "textarea", rows: 3, full: true, hint: "AI-generated prompt — copy/paste into your image generator, then upload the result above." },
        { key: "featured_image_alt", label: "Featured Image Alt Text", hint: "Descriptive alt text for accessibility + SEO." },
        { key: "faq_items", label: "FAQ Items (renders an in-page FAQ + FAQPage JSON-LD schema)", type: "faqItems", full: true },
        { key: "published", label: "Legacy 'published' flag (keep ON when Status = Published)", type: "boolean" },
      ]}
      listFields={[
        { key: "title", primary: true },
        { key: "category", label: "Category" },
        { key: "author", label: "Author" },
        { key: "read_time", label: "Read time (min)" },
      ]}
    />
  );
}

export function CityManager() {
  return (
    <ContentManager
      resource="cities"
      title="City Pages"
      description="Add and edit Ohio city landing pages. Use 'Duplicate' to create a new city from an existing template."
      enableDuplicate
      duplicateFields={[
        { key: "city_name", label: "New city name", placeholder: "e.g., Mentor" },
        { key: "county", label: "County", placeholder: "e.g., Lake" },
        { key: "slug", label: "URL slug (optional)", placeholder: "auto-generated from name if blank" },
      ]}
      defaults={{ published: true, landmarks: [], highways: [], hospitals: [] }}
      fields={[
        { key: "city_name", label: "City Name" },
        { key: "slug", label: "URL Slug" },
        { key: "county", label: "County" },
        { key: "population", label: "Population" },
        { key: "intro", label: "Intro Paragraph", type: "textarea", rows: 3, full: true },
        { key: "overview", label: "Overview", type: "textarea", rows: 5, full: true },
        { key: "landmarks", label: "Landmarks (one per line)", type: "list", full: true },
        { key: "highways", label: "Highways (one per line)", type: "list", full: true },
        { key: "hospitals", label: "Hospitals (one per line)", type: "list", full: true },
        { key: "accident_stats", label: "Accident Statistics", type: "textarea", rows: 3, full: true },
        { key: "local_info", label: "Local Info", type: "textarea", rows: 3, full: true },
        { key: "legal_notes", label: "Legal Venue Notes", type: "textarea", rows: 3, full: true },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2, full: true },
        { key: "published", label: "Published", type: "boolean" },
      ]}
      listFields={[
        { key: "city_name", primary: true },
        { key: "county", label: "County" },
        { key: "population", label: "Pop." },
      ]}
    />
  );
}

export function AccidentTypeManager() {
  return (
    <ContentManager
      resource="accident-types"
      title="Accident Type Pages"
      description="Manage landing pages for each accident type."
      enableDuplicate
      duplicateFields={[
        { key: "name", label: "New name", placeholder: "e.g., Bicycle Accidents" },
        { key: "slug", label: "URL slug (optional)" },
      ]}
      defaults={{ published: true, common_causes: [], common_injuries: [], what_to_do: [], order: 99, icon: "AlertTriangle" }}
      fields={[
        { key: "name", label: "Name" },
        { key: "slug", label: "URL Slug" },
        { key: "icon", label: "Lucide Icon Name", hint: "e.g., Car, Truck, Bike" },
        { key: "short_description", label: "Short Description", type: "textarea", rows: 2, full: true },
        { key: "intro", label: "Intro", type: "textarea", rows: 3, full: true },
        { key: "overview", label: "Overview", type: "textarea", rows: 5, full: true },
        { key: "common_causes", label: "Common Causes (one per line)", type: "list", full: true },
        { key: "common_injuries", label: "Common Injuries (one per line)", type: "list", full: true },
        { key: "what_to_do", label: "What to Do — Steps (one per line)", type: "list", full: true },
        { key: "legal_considerations", label: "Legal Considerations", type: "textarea", rows: 3, full: true },
        { key: "compensation_info", label: "Compensation Info", type: "textarea", rows: 3, full: true },
        { key: "statute_of_limitations", label: "Statute of Limitations", type: "textarea", rows: 2, full: true },
        { key: "order", label: "Order", type: "number" },
        { key: "seo_title", label: "SEO Title" },
        { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2, full: true },
        { key: "published", label: "Published", type: "boolean" },
      ]}
      listFields={[
        { key: "name", primary: true },
        { key: "icon", label: "Icon" },
        { key: "order", label: "Order" },
      ]}
    />
  );
}

export function FAQManager() {
  return (
    <ContentManager
      resource="faqs"
      title="FAQs"
      description="Manage frequently asked questions."
      defaults={{ published: true, category: "general", order: 99 }}
      fields={[
        { key: "question", label: "Question", full: true },
        { key: "answer", label: "Answer", type: "textarea", rows: 4, full: true },
        { key: "category", label: "Category" },
        { key: "order", label: "Order", type: "number" },
        { key: "published", label: "Published", type: "boolean" },
      ]}
      listFields={[
        { key: "question", primary: true },
        { key: "category", label: "Category" },
        { key: "order", label: "Order" },
      ]}
    />
  );
}

export function TestimonialManager() {
  return (
    <ContentManager
      resource="testimonials"
      title="Testimonials"
      description="Manage customer testimonials shown on the site."
      defaults={{ published: true, rating: 5, order: 99 }}
      fields={[
        { key: "name", label: "Name" },
        { key: "location", label: "Location", placeholder: "e.g., Columbus, OH" },
        { key: "rating", label: "Rating (1-5)", type: "number" },
        { key: "quote", label: "Quote", type: "textarea", rows: 4, full: true },
        { key: "accident_type", label: "Accident Type" },
        { key: "avatar", label: "Avatar Image URL" },
        { key: "order", label: "Order", type: "number" },
        { key: "published", label: "Published", type: "boolean" },
      ]}
      listFields={[
        { key: "name", primary: true },
        { key: "location", label: "Location" },
        { key: "accident_type", label: "Type" },
      ]}
    />
  );
}
